import {Component, computed, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {CodaltComponent} from '../codalt.component';
import {Routenames} from '../classes/route-name.enum';
import {ConfirmDialogService} from '../services/confirm-dialog-service/confirm-dialog.service';
import {LeaveService} from '../services/leave.service';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {Leave, LeaveType} from '../classes/leave';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {DatePipe} from '@angular/common';
import {DatetimeBeforePipe} from '../pipes/datetime-before.pipe';
import {IsOtherDatePipe} from '../pipes/other-date.pipe';
import {User} from '../classes/user.class';
import {CodaltDatePipe} from '../pipes/codalt-date.pipe';
import {UserService} from '../services/user/user.service';
import {HasRolePipe} from '../pipes/has-role.pipe';
import {
    AvatarComponent,
    AvatarStackComponent,
    DataListComponent,
    DataRowComponent,
    DayGroupComponent,
    EmptyStateComponent,
    IconTileComponent,
    PageComponent,
    PageHeaderComponent,
    StatusDotComponent
} from '../shared/ui';
import {hoursLabel, leaveTypeStyle, LeaveTypeStyle, WEEKDAYS} from './leave-type-style';

/** A leave in the list, with the derived texts of the design (presentation only). */
interface LeaveRow {
    leave: Leave;
    type: LeaveType | undefined;
    style: LeaveTypeStyle;
    running: boolean;
    past: boolean;
    times: string;
    /** "Elke maandag, woensdag" for repeating leave */
    repeat: string | null;
    /** "20 dagen", "8:00 uur" or "3 werkdagen · 24:00 uur" */
    summary: string;
    userNames: string;
    /** First three users for the avatar stack */
    stackUsers: User[];
}

/** Either the "Historie" heading or a leave row */
interface LeaveListItem {
    key: string;
    head?: string;
    row?: LeaveRow;
}

@Component({
    selector: 'app-leave',
    templateUrl: './leave.component.html',
    styleUrls: ['./leave.component.scss'],
    imports: [
        MatCheckbox,
        ReactiveFormsModule,
        MatButton,
        MatIconButton,
        MatTooltip,
        DatePipe,
        RouterLink,
        DatetimeBeforePipe,
        IsOtherDatePipe,
        CodaltDatePipe,
        HasRolePipe,
        PageComponent,
        PageHeaderComponent,
        DataListComponent,
        DataRowComponent,
        DayGroupComponent,
        EmptyStateComponent,
        IconTileComponent,
        AvatarComponent,
        AvatarStackComponent,
        StatusDotComponent
    ]
})
export class LeaveComponent extends CodaltComponent implements OnInit {
    readonly leaves = signal<Leave[]>(null);
    readonly leaveTypes = signal<LeaveType[]>(null);
    readonly users = signal<User[]>(null);

    now = new Date();
    fcShowHistoric = new FormControl(false);

    /** Current leave first; past leave (only returned with "Toon historie") under its own heading, newest first. */
    readonly items = computed<LeaveListItem[]>(() => {
        const types = this.leaveTypes() ?? [];
        const rows = (this.leaves() ?? []).map(leave => this.toRow(leave, types));
        const current = rows.filter(row => !row.past);
        const past = rows.filter(row => row.past).reverse();
        return [
            ...current.map(row => ({key: `${row.leave.id}`, row})),
            ...(past.length ? [{key: 'historie', head: 'Historie'}] : []),
            ...past.map(row => ({key: `${row.leave.id}`, row}))
        ];
    });

    public constructor(
        private confirmDialog: ConfirmDialogService,
        private leaveService: LeaveService,
        private userService: UserService,
    ) {
        super();
        this.crumbs = [
            {name: 'Verlof', route: Routenames.leave}
        ];
    }

    ngOnInit() {
        this.subscriptions.add(
            this.userService.getList().subscribe(users => {
                this.users.set(users);
            })
        );
        this.subscriptions.add(
            this.leaveService.getLeaveTypes().subscribe(leaveTypes => {
                this.leaveTypes.set(leaveTypes);
            })
        );

        this.getLeaves();

        this.subscriptions.add(
            this.fcShowHistoric.valueChanges.subscribe(() => {
                this.getLeaves();
            })
        );

        this.subscriptions.add(
            this.leaveService.getLeaves().subscribe({
                next: (leaves) => this.leaves.set(leaves),
                error: err => console.error(err)
            })
        )
    }

    delete(event, leave?: Leave) {
        event.stopPropagation();

        this.confirmDialog.confirm(
            'Verlof verwijderen',
            `Weet u zeker dat u verlof ${leave.comment} wilt verwijderen?`,
            'Verwijderen',
            'Annuleren')
            .then(() => {
                this.leaveService.deleteLeave(leave.id).subscribe(() => {
                    this.leaves.update(leaves => leaves.filter(leaveFilter => leaveFilter.id !== leave.id));
                });
            });
    }

    approve(event, leave?: Leave) {
        event.stopPropagation();
        this.subscriptions.add(
            this.leaveService.approveLeave(leave.id).subscribe(response => {
                this.leaves.update(leaves => leaves.map(l => l.id === response.data.id ? response.data : l));
            })
        );
    }

    getLeaves() {
        this.subscriptions.add(
            this.leaveService.getLeaves(this.fcShowHistoric.value).subscribe(leaves => {
                this.leaves.set(leaves);
            })
        );
    }

    private toRow(leave: Leave, types: LeaveType[]): LeaveRow {
        const begin = new Date(leave.begindate);
        const end = new Date(leave.enddate);
        const dayMinutes = Math.max(0, timeToMinutes(leave.endtime) - timeToMinutes(leave.begintime));
        const days = WEEKDAYS.filter(day => leave[`repeat_${day.key}` as const]);
        const repeating = leave.interval_week > 0 && days.length > 0;

        let repeat: string = null;
        let summary: string;
        if (repeating) {
            const names = days.map(day => day.name).join(', ');
            repeat = leave.interval_week > 1 ? `Elke ${leave.interval_week} weken op ${names}` : `Elke ${names}`;
            const count = countRepeatDays(begin, end, days.map(day => day.key), leave.interval_week);
            summary = count === 1 ? '1 dag' : `${count} dagen`;
        } else if (begin.toDateString() === end.toDateString()) {
            summary = `${hoursLabel(dayMinutes)} uur`;
        } else {
            const workdays = countWorkdays(begin, end);
            summary = `${workdays} ${workdays === 1 ? 'werkdag' : 'werkdagen'} · ${hoursLabel(workdays * dayMinutes)} uur`;
        }

        return {
            leave,
            type: types.find(type => type.key === leave.leave_type),
            style: leaveTypeStyle(leave.leave_type),
            running: begin < this.now && !(end < this.now),
            past: end < this.now,
            times: `${(leave.begintime ?? '').slice(0, 5)}–${(leave.endtime ?? '').slice(0, 5)}`,
            repeat,
            summary,
            userNames: (leave.users ?? []).map(user => user.name).join(', '),
            stackUsers: (leave.users ?? []).slice(0, 3)
        };
    }
}

function timeToMinutes(time: string | null | undefined): number {
    const [hours, minutes] = (time ?? '0:0').split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
}

function dayStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function countWorkdays(begin: Date, end: Date): number {
    let count = 0;
    for (const day = dayStart(begin); day <= end; day.setDate(day.getDate() + 1)) {
        if (day.getDay() > 0 && day.getDay() < 6) {
            count++;
        }
    }
    return count;
}

/** Dates between begin and end on the given weekdays, every `interval` weeks counted from the first week. */
function countRepeatDays(begin: Date, end: Date, weekdays: string[], interval: number): number {
    const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const firstMonday = dayStart(begin);
    firstMonday.setDate(firstMonday.getDate() - ((firstMonday.getDay() + 6) % 7));
    let count = 0;
    for (const day = dayStart(begin); day <= end && count < 1000; day.setDate(day.getDate() + 1)) {
        const week = Math.floor(Math.round((day.getTime() - firstMonday.getTime()) / 864e5) / 7);
        if (weekdays.includes(keys[day.getDay()]) && week % interval === 0) {
            count++;
        }
    }
    return count;
}
